import {Button, Grid} from '@mui/material';
import React from 'react';
import {useMutation} from '@apollo/client';
import {Box} from '@mui/system';
import {useSnackbar} from 'notistack';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {
  ReportPackGenericFormType,
  reportPackFormType,
  ReportPackGenericFormInnerType,
  reportPackContributionAmountField,
  reportPackContributionFrequencyTypeField,
  reportPackSIPPNumberField,
  reportPackContributionPaymentDateTypeField,
  reportPackContributionSourceTypeField,
  reportPackContributionTypeField,
  reportPackAdviceReceivedField,
} from './ReportPackFields';
import {upsertSippContributionForm} from './reportPackQueries';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {
  UpsertSIPPContributionForm,
  UpsertSIPPContributionFormVariables,
} from '../../schema-types/UpsertSIPPContributionForm';
import {getNumberOrNull} from '../../utils/getNumberOrNull';
import {ContributionTypes} from '../../schema-types/globalTypes';
import {getReportPackMissingFieldError} from './getReportPackMissingFieldError';
import {ReportPackFormFragment} from '../../schema-types/ReportPackFormFragment';

export const ReportPackSIPPContribution: React.FC<ReportPackGenericFormType> = ({
  formData,
  onClose,
  disabled,
}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [onUpsertSippContributionForm, {loading: onUpsertSIPPContributionFormLoading}] =
    useMutation<UpsertSIPPContributionForm, UpsertSIPPContributionFormVariables>(
      upsertSippContributionForm
    );
  return (
    <ReportPackSIPPContributionFormWrapped
      formFields={[]}
      disabled={disabled}
      submit={async (values) => {
        if (disabled) {
          return;
        }
        closeSnackbar();
        enqueueSnackbar(`Saving ${formData.formTypeLabel}...`, {
          variant: 'info',
        });
        await onUpsertSippContributionForm({
          variables: {
            contactCuroId: values.contactCuroId,
            reportPackId: String(formData.reportPackId),
            formId: formData.id,
            adviceReceived: values.adviceReceived,
            contributionAmount: getNumberOrNull(values.contributionAmount),
            contributionFrequencyType: values.contributionFrequencyType,
            contributionPaymentDateType: values.contributionPaymentDateType,
            contributionSourceType: values.contributionSourceType,
            contributionType: values.contributionType,
            sippNumber: values.sippNumber,
          },
        })
          .then(({data}) => {
            if (data?.upsertSippContributionForm?.ok) {
              closeSnackbar();
              enqueueSnackbar(`Successfully saved ${formData.formTypeLabel}`, {
                variant: 'success',
              });
              onClose();
            } else {
              throw Error(data?.upsertSippContributionForm?.errors?.join(','));
            }
          })
          .catch((err) => {
            closeSnackbar();
            enqueueSnackbar(`Could not save ${formData.formTypeLabel} ${err}`, {
              variant: 'error',
            });
          });
      }}
      initialValueOverrides={{
        ...formData,
        reportPackId: String(formData.reportPackId),
        contactCuroId: String(formData.contactCuroId),
      }}
    />
  );
};

const ReportPackSIPPContributionForm: React.FC<ReportPackGenericFormInnerType> = ({
  disabled,
}) => {
  const {submitForm, isSubmitting, values} = useAutoForm<ReportPackFormFragment>();

  return (
    <Box maxWidth={500}>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackAdviceReceivedField.id
            )}
            field={reportPackAdviceReceivedField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackContributionAmountField.id
            )}
            field={reportPackContributionAmountField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackSIPPNumberField.id)}
            field={reportPackSIPPNumberField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackContributionSourceTypeField.id
            )}
            field={reportPackContributionSourceTypeField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackContributionTypeField.id
            )}
            field={reportPackContributionTypeField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackContributionFrequencyTypeField.id
            )}
            field={{
              ...reportPackContributionFrequencyTypeField,
              disabled: values.contributionType === ContributionTypes.SINGLE,
            }}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackContributionPaymentDateTypeField.id
            )}
            field={{
              ...reportPackContributionPaymentDateTypeField,
              disabled: values.contributionType === ContributionTypes.SINGLE,
            }}
          />
        </Grid>
      </Grid>
      <Box position="absolute" bottom={20} right={20}>
        {!disabled && (
          <Button
            variant="contained"
            color="secondary"
            disabled={isSubmitting}
            onClick={() => submitForm()}
          >
            Save form
          </Button>
        )}
      </Box>
    </Box>
  );
};

const ReportPackSIPPContributionFormWrapped = withAutoFormNew<
  reportPackFormType,
  ReportPackGenericFormInnerType
>(ReportPackSIPPContributionForm, {}, {debugPosition: 'bottom'});
